import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
 parse, parseISO, isAfter, formatDistance,
} from 'date-fns';

/**
 * ThanksPage component
 *
 * Displays a celebratory message after a source request has been posted.
 * Minimal and clean design focused on next steps.
 *
 * Props:
 *   - slug (string): Slug for public request view URL
 *   - schedule (string): "now" for immediate or "custom" for scheduled
 *   - customScheduleDateTime (string): ISO datetime for custom schedule
 *   - timeNow (number): Current Unix timestamp for calculations
 */
function ThanksPage({
 slug, migrateId, viewRequestUrlTemplate, schedule, customScheduleDateTime, timeNow,
}) {
  const handleCreateAnother = useCallback(() => {
    window.location.reload();
  }, []);

  // Replace :id in the template with actual ID (migrateId preferred, slug as fallback)
  const viewRequestUrl = viewRequestUrlTemplate.replace(':id', migrateId || slug);

  // Determine post time and messaging
  let postMessage = 'Experts and media professionals are already receiving your request.';
  let requestHeadline = 'live';
  let headlineIcon = 'fa-circle-check text-success';

  if (schedule === 'custom' && customScheduleDateTime) {
    // Handle both ISO format (from tests) and datetime-local format (from form input)
    let postTime;
    if (customScheduleDateTime.includes('.')) {
      // Full ISO string with milliseconds (from tests)
      postTime = parseISO(customScheduleDateTime);
    } else {
      // datetime-local format from form (YYYY-MM-DDTHH:mm)
      postTime = parse(customScheduleDateTime, "yyyy-MM-dd'T'HH:mm", new Date());
    }
    const now = new Date(timeNow * 1000);

    if (isAfter(postTime, now)) {
      const timeString = formatDistance(postTime, now, { addSuffix: true });
      postMessage = `Your request will be shared with experts and media professionals ${timeString}.`;
      requestHeadline = 'scheduled';
      headlineIcon = 'fa-clock text-warning';
    }
  }

  return (
    <div className="mx-5 my-4">
      <div className="shadow-sm card px-5 pt-5 pb-4">
        <div className="text-center mb-5">
          <div className="mb-4">
            <i className={`fa-solid ${headlineIcon}`} style={{ fontSize: '56px' }} />
          </div>
          <h1 className="fw-semibold mb-3">Your request is {requestHeadline}!</h1>
          <p className="text-muted lead mb-0">{postMessage}</p>
        </div>
        <div className="row justify-content-center gap-3 mb-5">
          <div className="col-auto">
            <a href={viewRequestUrl} className="btn btn-primary btn-lg">
              <i className="fa-solid fa-eye me-2" />
              View Request
            </a>
          </div>
          <div className="col-auto">
            <button className="btn btn-outline-primary btn-lg" type="button" onClick={handleCreateAnother}>
              <i className="fa-solid fa-plus me-2" />
              Create Another Request
            </button>
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 text-center">
            <p className="text-muted small mb-0">
              Tip: Share your request on LinkedIn to reach more professionals in your network.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

ThanksPage.propTypes = {
  slug: PropTypes.string.isRequired,
  migrateId: PropTypes.number,
  viewRequestUrlTemplate: PropTypes.string.isRequired,
  schedule: PropTypes.string.isRequired,
  customScheduleDateTime: PropTypes.string,
  timeNow: PropTypes.number.isRequired,
};

ThanksPage.defaultProps = {
  migrateId: null,
  customScheduleDateTime: null,
};

export default ThanksPage;
